"use client";

import { useTranslations } from "next-intl";
import { useState } from "react";
import { Box, Button, IconButton, Paper, Typography } from "@/components/controls/base-controls";
import { ChevronLeftIcon, ChevronRightIcon } from "@/components/controls/icons";

type DayObject = {
  dayNumber: number;
  date: Date;
  isCurrentMonth: boolean;
  isPreviousMonth: boolean;
  isNextMonth: boolean;
};

type DatePickerProps = {
  disabled?: { before?: Date; after?: Date }[];
  mode: "single" | "range";
  selectedDates: Date[];
  onClose: (date: Date[]) => void;
};

const DatePicker = ({ disabled, mode, selectedDates, onClose }: DatePickerProps) => {
  const t = useTranslations();

  const today = new Date();

  const earliestDate = disabled?.[0]?.before ? new Date(disabled[0].before).setHours(0, 0, 0, 0) : undefined;
  const latestDate = disabled?.[0]?.after ? new Date(disabled[0].after).setHours(0, 0, 0, 0) : undefined;

  // Manage calendar view tracking
  const [currentDate, setCurrentDate] = useState<Date>(
    selectedDates && selectedDates.length > 0  && selectedDates[0]
    ? selectedDates[0]
    : new Date()
  ); 
  const [selectedDate, setSelectedDate] = useState<Date[]>(selectedDates);

  const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // 1. Core Month Calculations
  const firstDayOfMonth = new Date(year, month, 1);
  const lastDayOfMonth = new Date(year, month + 1, 0);
  
  const daysInCurrentMonth = lastDayOfMonth.getDate();
  const startWeekday = firstDayOfMonth.getDay(); // 0 = Sunday, 1 = Monday...
  const endWeekday = lastDayOfMonth.getDay();

  const daysGrid: DayObject[] = [];

  // 2. Push Trailing Days from Previous Month (Shaded)
  const prevMonthLastDate = new Date(year, month, 0).getDate();
  for (let i = startWeekday - 1; i >= 0; i--) {
    const prevDay = prevMonthLastDate - i;
    daysGrid.push({
      dayNumber: prevDay,
      date: new Date(year, month - 1, prevDay, 0, 0, 0, 0),
      isCurrentMonth: false,
      isPreviousMonth: true,
      isNextMonth: false
    });
  }

  // 3. Push Days of Current Month
  for (let i = 1; i <= daysInCurrentMonth; i++) {
    daysGrid.push({
      dayNumber: i,
      date: new Date(year, month, i, 0, 0, 0, 0),
      isCurrentMonth: true,
      isPreviousMonth: false,
      isNextMonth: false
    });
  }

  // 4. Push Trailing Days from Next Month (Lighter Shade)
  const daysNeededFromNextMonth = 6 - endWeekday;
  for (let i = 1; i <= daysNeededFromNextMonth; i++) {
    daysGrid.push({
      dayNumber: i,
      date: new Date(year, month + 1, i, 0, 0, 0, 0),
      isCurrentMonth: false,
      isPreviousMonth: false,
      isNextMonth: true
    });
  };

  // Month navigation helpers
  const handlePrevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const handleNextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const isPrevDisabled = !!disabled?.some(
    (d) => d.before && new Date(year, month, 1) <= new Date(d.before.getFullYear(), d.before.getMonth(), 1)
  );
  const isNextDisabled = !!disabled?.some(
    (d) => d.after && new Date(year, month, 1) >= new Date(d.after.getFullYear(), d.after.getMonth(), 1)
  );

  // Check if two dates match on day, month, and year
  const isSameDay = (date1: Date, date2: Date | undefined) => {
    if (!date2) return false;
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const handleDateSelected = (value: Date) => {
    const clickedDate = new Date(value);
    clickedDate.setHours(0, 0, 0, 0);

    if (mode === "single" || !selectedDate) {
      setSelectedDate([clickedDate]);
    } else {
      // Range mode selection routing rules
      if (selectedDate.length === 0 || selectedDate.length === 2) {
        // Start a fresh range selection loop
        setSelectedDate([clickedDate]);
      } else {
        const firstSelection = selectedDate[0];
        if (!firstSelection) {
          setSelectedDate([clickedDate]);
          return;
        }

      if (clickedDate < firstSelection) {
        // Inversion Check: If user selects an earlier date, make it the new start date
        setSelectedDate([clickedDate]);
      } else {
        // Clean Range bounds locked down perfectly with zero type warnings
        setSelectedDate([firstSelection, clickedDate]);
      }
      }
    }
  };

  return (
    <Paper elevation={2} sx={{ width: "320px", p: "1em 1em .5em 1em", borderRadius: 2, bgcolor: 'background.paper' }}>
      {/* Header */}
      <Box sx={{
        display:"flex", justifyContent:"space-between", alignItems:"center", mb:3, pb:2, borderBottom:"1px solid", borderColor:"divider"
      }} >
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {currentDate.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
        </Typography>
        <Box>
          <IconButton 
            size="small" 
            onClick={handlePrevMonth}
            disabled={isPrevDisabled}
            sx={{ 
              bgcolor: isPrevDisabled ? "transparent" : "primary.fadedBg", 
              mr: 1,
              "&.Mui-disabled": { bgcolor: "transparent" },
              "&:hover": { bgcolor: isPrevDisabled ? "transparent" : "primary.fadedBg" }
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={handleNextMonth}
            disabled={isNextDisabled}
            sx={{ 
              bgcolor: isNextDisabled ? "transparent" : "primary.fadedBg",
              "&.Mui-disabled": { bgcolor: "transparent" },
              "&:hover": { bgcolor: isNextDisabled ? "transparent" : "primary.fadedBg" }
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Grid Container */}
      <Box  sx={{
        display:"grid", gridTemplateColumns: "repeat(7, 1fr)",
        gap:1.2, justifyItems:"center", alignItems:"center"
      }} >
        {/* Weekday Titles */}
        {weekdays.map((day) => (
          <Typography key={day} variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', mb: 1 }}>
            {day}
          </Typography>
        ))}

        {/* Render Formatted Days */}
        {daysGrid.map((item, _idx) => {
          const itemTime = item.date.getTime();
          const isSelected = selectedDate.some(date => isSameDay(item.date, date));
          
          const startRangeTime = selectedDate[0]?.getTime() || 0;
          const endRangeTime = selectedDate[1]?.getTime() || 0;
          
          // 🔑 FIXED: Pure calculation parameters utilizing getTime benchmarks avoiding .setHours mutations
          const isInbetween = mode === "range" && selectedDate.length === 2 && itemTime > startRangeTime && itemTime < endRangeTime;
          const isToday = isSameDay(item.date, today);

          // Evaluate boundary restrictions safely
          const isBeforeEarliest = earliestDate ? itemTime < earliestDate : false;
          const isAfterLatest = latestDate ? itemTime > latestDate : false;
          const isRangeBlocked = mode === "range" && selectedDate.length === 1 && itemTime < startRangeTime;
          const isDisabled = isBeforeEarliest || isAfterLatest || isRangeBlocked;

          let cellBgColor = 'transparent';
          let cellTextColor = 'text.primary';
          let opacity = 1;

          if (isSelected) {
            cellBgColor = 'primary.main';
            cellTextColor = 'primary.contrastText';
          } else if (isInbetween) {
            cellBgColor = 'primary.fadedBg'; // Subtle contrast for range paths
            cellTextColor = 'primary.contrastText';
          } else if (isDisabled) {
            cellTextColor = 'text.disabled';
            opacity = 0.35;
          } else if (item.isPreviousMonth || item.isNextMonth) {
            cellTextColor = 'text.secondary';
            opacity = 0.5;
          }

          return (
            <Box 
              key={item.date.toISOString()} 
              onClick={() => !isDisabled && handleDateSelected(item.date)}
              sx={{ 
                width: '100%', 
                maxWidth: 320 / 7, 
                aspectRatio: '1 / 1', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                borderRadius: '50%', 
                cursor: isDisabled ? 'default' : 'pointer',
                pointerEvents: isDisabled ? 'none' : 'auto',
                fontSize: '0.75rem', 
                fontWeight: isSelected ? 600 : item.isCurrentMonth ? 500 : 400, 
                transition: 'all 0.15s ease-in-out', 
                bgcolor: cellBgColor, 
                border: isToday && !isSelected ? `1px solid black` : '1px solid transparent', 
                color: cellTextColor, 
                opacity: opacity,
                boxShadow: isSelected ? '0px 4px 10px rgba(25, 118, 210, 0.35)' : 'none',
                '&:hover': { 
                  bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                  opacity: 1 
                }, 
              }} 
            >
              {item.dayNumber}
            </Box>
          );
        })}
      </Box>

      <Box sx={{
        display:"flex",
        justifyContent:"flex-end",
        gap:2,
        mt:1,
        pt:1,
        borderTop:"1px solid",
        borderColor:"divider",
      }}
        
      >
        {/* Clear Button */}
        <Button
          variant="text"
          sx={{
            fontSize: "0.75em",
            fontWeight: 600,
            padding: "1.em 1em",
          }}
          onClick={() => {
            setSelectedDate([]); // Wipe out internal visual state selection
          }}
                >
          {t("calendar.clear")}
        </Button>

        {/* Done Button */}
        <Button 
          variant="darkBg" 
          disableElevation // Gives it a clean modern flat look matching contemporary date pickers
          onClick={() => onClose(selectedDate)}
          sx={{ 
            fontSize: "0.75em",
            fontWeight: 600,
            padding: "1.em 1em"
          }}
        >
          {t("calendar.done")}
        </Button>
      </Box>
    </Paper>
  );
};

export default DatePicker;
